import React, { useState, createContext, useContext } from 'react';
import { Insight } from '../components/InsightDetailModal';
export interface KPI {
  id: string;
  title: string;
  metric: string;
  type: 'leading' | 'lagging';
  currentValue: number;
  targetValue: number;
  baselineValue: number;
  unit: string; // '%', 'hrs', 'count', 'rate'
  targetDate: string;
  status: 'on_track' | 'at_risk' | 'behind' | 'achieved';
  owner: string;
  linkedInsightId?: string;
  linkedInsightTitle?: string;
  createdAt: Date;
  history: {
    date: string;
    value: number;
  }[];
}
interface KPIContextType {
  kpis: KPI[];
  addKPI: (kpi: Omit<KPI, 'id' | 'createdAt' | 'history'>) => void;
  updateKPI: (id: string, updates: Partial<KPI>) => void;
  removeKPI: (id: string) => void;
  addKPIFromInsight: (insight: Insight) => void;
}
const MOCK_KPIS: KPI[] = [{
  id: 'kpi-1',
  title: 'Monday PTP Completion Rate',
  metric: 'PTP Completion %',
  type: 'leading',
  baselineValue: 40,
  currentValue: 52,
  targetValue: 80,
  unit: '%',
  targetDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  status: 'on_track',
  owner: 'Safety Team',
  linkedInsightId: 'i1',
  linkedInsightTitle: 'PTP engagement drops 30% on Mondays across all sites',
  createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
  history: [{
    date: 'W1',
    value: 40
  }, {
    date: 'W2',
    value: 42
  }, {
    date: 'W3',
    value: 48
  }, {
    date: 'W4',
    value: 52
  }]
}, {
  id: 'kpi-2',
  title: 'New Worker Mentorship Coverage',
  metric: 'Mentorship %',
  type: 'leading',
  baselineValue: 20,
  currentValue: 25,
  targetValue: 90,
  unit: '%',
  targetDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  status: 'behind',
  owner: 'Site Supervisors',
  linkedInsightId: 'i2',
  linkedInsightTitle: 'SteelCo Inc has 3x more new-worker risk flags than average',
  createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
  history: [{
    date: 'W1',
    value: 20
  }, {
    date: 'W2',
    value: 22
  }, {
    date: 'W3',
    value: 25
  }]
}, {
  id: 'kpi-3',
  title: 'Average Risk Closure Time',
  metric: 'Closure Time',
  type: 'leading',
  baselineValue: 36,
  currentValue: 28,
  targetValue: 24,
  unit: 'hrs',
  targetDate: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  status: 'at_risk',
  owner: 'Project Managers',
  createdAt: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000),
  history: [{
    date: 'W1',
    value: 36
  }, {
    date: 'W2',
    value: 34
  }, {
    date: 'W3',
    value: 32
  }, {
    date: 'W4',
    value: 28
  }]
}, {
  id: 'kpi-4',
  title: 'Afternoon Inspection Score',
  metric: 'Inspection Score',
  type: 'leading',
  baselineValue: 62,
  currentValue: 71,
  targetValue: 85,
  unit: '%',
  targetDate: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  status: 'on_track',
  owner: 'Quality Control',
  linkedInsightId: 'i3',
  linkedInsightTitle: 'Inspection scores decline after 3pm across all projects',
  createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
  history: [{
    date: 'W1',
    value: 62
  }, {
    date: 'W2',
    value: 68
  }, {
    date: 'W3',
    value: 71
  }]
}, {
  id: 'kpi-5',
  title: 'Total Recordable Incident Rate',
  metric: 'TRIR',
  type: 'lagging',
  baselineValue: 3.2,
  currentValue: 2.8,
  targetValue: 2.0,
  unit: 'rate',
  targetDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  status: 'on_track',
  owner: 'Corporate Safety',
  createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
  history: [{
    date: 'M1',
    value: 3.2
  }, {
    date: 'M2',
    value: 3.0
  }, {
    date: 'M3',
    value: 2.8
  }]
}, {
  id: 'kpi-6',
  title: 'Lost Time Incident Rate',
  metric: 'LTIR',
  type: 'lagging',
  baselineValue: 1.5,
  currentValue: 1.6,
  targetValue: 1.0,
  unit: 'rate',
  targetDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  status: 'at_risk',
  owner: 'Corporate Safety',
  createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
  history: [{
    date: 'M1',
    value: 1.5
  }, {
    date: 'M2',
    value: 1.4
  }, {
    date: 'M3',
    value: 1.6
  }]
}];
const KPIContext = createContext<KPIContextType | undefined>(undefined);
export function KPIProvider({
  children
}: {
  children: ReactNode;
}) {
  const [kpis, setKpis] = useState<KPI[]>(MOCK_KPIS);
  const addKPI = (kpiData: Omit<KPI, 'id' | 'createdAt' | 'history'>) => {
    const newKPI: KPI = {
      ...kpiData,
      id: `kpi-${Date.now()}`,
      createdAt: new Date(),
      history: [{
        date: 'Start',
        value: kpiData.baselineValue
      }]
    };
    setKpis(prev => [newKPI, ...prev]);
  };
  const updateKPI = (id: string, updates: Partial<KPI>) => {
    setKpis(prev => prev.map(kpi => kpi.id === id ? {
      ...kpi,
      ...updates
    } : kpi));
  };
  const removeKPI = (id: string) => {
    setKpis(prev => prev.filter(kpi => kpi.id !== id));
  };
  const addKPIFromInsight = (insight: Insight) => {
    // Check if already tracked
    if (kpis.some(k => k.linkedInsightId === insight.id)) return;
    const newKPI: KPI = {
      id: `kpi-${Date.now()}`,
      title: `Improve: ${insight.title.substring(0, 30)}...`,
      metric: insight.category === 'ptp' ? 'PTP Compliance' : 'Risk Score',
      type: 'leading',
      baselineValue: 0,
      currentValue: 0,
      targetValue: Math.abs(insight.impactScore),
      unit: '%',
      targetDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      status: 'on_track',
      owner: 'Unassigned',
      linkedInsightId: insight.id,
      linkedInsightTitle: insight.title,
      createdAt: new Date(),
      history: [{
        date: 'Start',
        value: 0
      }]
    };
    setKpis(prev => [newKPI, ...prev]);
  };
  return <KPIContext.Provider value={{
    kpis,
    addKPI,
    updateKPI,
    removeKPI,
    addKPIFromInsight
  }} data-id="element-4094" data-mp="2ks1ty1iteu38">
      {children}
    </KPIContext.Provider>;
}
export function useKPIs() {
  const context = useContext(KPIContext);
  if (context === undefined) {
    throw new Error('useKPIs must be used within a KPIProvider');
  }
  return context;
}